import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface RecruitmentFormProps {
  domain: string;
}

const RecruitmentForm: React.FC<RecruitmentFormProps> = ({ domain }) => {
  const [formData, setFormData] = useState({
    candidateName: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    qualification: '',
    experience: '',
    currentEmployer: '',
    expectedSalary: '',
    skills: '',
    address: '',
    city: '',
    state: '',
    referredBy: '',
    coverNote: '',
  });

  const [submitting, setSubmitting] = useState(false);

  const departments = [
    'Mathematics',
    'Science',
    'English',
    'Hindi',
    'Social Studies',
    'Computer Science',
    'Physical Education',
    'Arts & Music',
    'Administration',
    'Accounts',
    'Library',
    'Transport',
    'Maintenance',
    'Other',
  ];

  const positions = [
    'Teacher',
    'Senior Teacher',
    'Head of Department',
    'Vice Principal',
    'Lab Assistant',
    'Librarian',
    'Accountant',
    'Office Assistant',
    'Clerk',
    'Peon',
    'Driver',
    'Security Guard',
    'Other',
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const generateApplicationId = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `REC${year}${random}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.candidateName || !formData.email || !formData.phone || !formData.position || !formData.department) {
      alert('Please fill all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const applicationId = generateApplicationId();

      await addDoc(collection(db, `school-data/${domain}/recruitment-applications`), {
        applicationId,
        ...formData,
        status: 'pending',
        appliedOn: new Date().toISOString(),
      });

      alert(`Application submitted successfully!\nApplication ID: ${applicationId}`);

      setFormData({
        candidateName: '',
        email: '',
        phone: '',
        position: '',
        department: '',
        qualification: '',
        experience: '',
        currentEmployer: '',
        expectedSalary: '',
        skills: '',
        address: '',
        city: '',
        state: '',
        referredBy: '',
        coverNote: '',
      });
    } catch (error: any) {
      console.error('Error submitting application:', error);
      alert(`Failed to submit application: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      candidateName: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      qualification: '',
      experience: '',
      currentEmployer: '',
      expectedSalary: '',
      skills: '',
      address: '',
      city: '',
      state: '',
      referredBy: '',
      coverNote: '',
    });
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-file-earmark-plus me-2 text-primary-custom"></i>
          New Recruitment Application
        </h5>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <h6 className="mb-3 text-primary-custom">Candidate Information</h6>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Candidate Name *</label>
              <input type="text" className="form-control" name="candidateName" value={formData.candidateName} onChange={handleChange} required />
            </div>
            <div className="col-md-4">
              <label className="form-label">Email *</label>
              <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="col-md-4">
              <label className="form-label">Phone *</label>
              <input type="tel" className="form-control" name="phone" value={formData.phone} onChange={handleChange} required />
            </div>
          </div>

          <h6 className="mb-3 text-primary-custom">Position Details</h6>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Position *</label>
              <select className="form-select" name="position" value={formData.position} onChange={handleChange} required>
                <option value="">Select Position</option>
                {positions.map((pos) => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Department *</label>
              <select className="form-select" name="department" value={formData.department} onChange={handleChange} required>
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Expected Salary</label>
              <input type="text" className="form-control" name="expectedSalary" value={formData.expectedSalary} onChange={handleChange} placeholder="e.g., 30,000/month" />
            </div>
          </div>

          <h6 className="mb-3 text-primary-custom">Qualifications & Experience</h6>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Highest Qualification</label>
              <input type="text" className="form-control" name="qualification" value={formData.qualification} onChange={handleChange} placeholder="e.g., B.Ed, M.Sc, MBA" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Total Experience</label>
              <input type="text" className="form-control" name="experience" value={formData.experience} onChange={handleChange} placeholder="e.g., 5 years" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Current Employer</label>
              <input type="text" className="form-control" name="currentEmployer" value={formData.currentEmployer} onChange={handleChange} placeholder="Current school/organization" />
            </div>
            <div className="col-12">
              <label className="form-label">Key Skills</label>
              <input type="text" className="form-control" name="skills" value={formData.skills} onChange={handleChange} placeholder="e.g., Classroom management, CBSE curriculum, Smart Board" />
            </div>
          </div>

          <h6 className="mb-3 text-primary-custom">Address & Additional Info</h6>
          <div className="row g-3 mb-4">
            <div className="col-12">
              <label className="form-label">Address</label>
              <textarea className="form-control" name="address" rows={2} value={formData.address} onChange={handleChange}></textarea>
            </div>
            <div className="col-md-4">
              <label className="form-label">City</label>
              <input type="text" className="form-control" name="city" value={formData.city} onChange={handleChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">State</label>
              <input type="text" className="form-control" name="state" value={formData.state} onChange={handleChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Referred By</label>
              <input type="text" className="form-control" name="referredBy" value={formData.referredBy} onChange={handleChange} placeholder="Name of referrer (if any)" />
            </div>
            <div className="col-12">
              <label className="form-label">Cover Note</label>
              <textarea className="form-control" name="coverNote" rows={3} value={formData.coverNote} onChange={handleChange} placeholder="Brief note about why the candidate is a good fit"></textarea>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Submitting...</>
              ) : (
                <><i className="bi bi-check-circle me-2"></i>Submit Application</>
              )}
            </button>
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              <i className="bi bi-x-circle me-2"></i>Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecruitmentForm;
